import React, { forwardRef, useState, useEffect } from 'react';
import { Input as HeroInput, Button } from '@heroui/react';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline';

export interface PasswordInputProps {
  label?: string;
  placeholder?: string;
  description?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  validate?: (value: string) => string | null;
  onChange?: (val: string) => void;
  onClear?: () => void;
  value?: string;
  size?: 'sm' | 'md' | 'lg' | 'md+';
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      label = '',
      placeholder = '',
      description = '',
      errorMessage = '',
      isRequired = true,
      isDisabled = false,
      isClearable = true,
      startContent,
      endContent,
      validate,
      onChange,
      onClear,
      value: propValue = '',
      size = 'md',
      ...rest
    },
    ref,
  ) => {
    const [value, setValue] = useState(propValue);
    const [error, setError] = useState<string | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      setValue(propValue);
    }, [propValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setValue(newValue);

      if (validate) {
        const validationError = validate(newValue);
        setError(validationError);
      }

      onChange?.(newValue);
    };

    // Determine if we should use the custom md+ size
    const isCustomMdPlus = size === 'md+';
    // Use 'md' for HeroUI's Input when our custom size is 'md+'
    const heroUISize = isCustomMdPlus ? 'md' : size;

    return (
      <HeroInput
        ref={ref}
        type={isVisible ? 'text' : 'password'}
        value={value}
        onChange={handleChange}
        label={<span style={{ color: '#6e6e6e' }}>{label}</span>}
        placeholder={placeholder}
        description={<span style={{ color: '#6e6e6e' }}>{description}</span>}
        errorMessage={error || errorMessage}
        isInvalid={!!error}
        isRequired={isRequired}
        isDisabled={isDisabled}
        isClearable={isClearable}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={() => {
          setIsFocused(false);
        }}
        size={heroUISize}
        color="default"
        variant="bordered"
        radius="md"
        labelPlacement="outside"
        startContent={startContent}
        endContent={
          <>
            {endContent}
            <Button
              isIconOnly
              variant="light"
              radius="full"
              size="sm"
              onClick={() => setIsVisible((prev) => !prev)}
              className="ml-1"
              tabIndex={-1}
              aria-label={isVisible ? 'Hide password' : 'Show password'}
            >
              {isVisible ? (
                <EyeOffIcon className="w-4 h-4" />
              ) : (
                <EyeIcon className="w-4 h-4" />
              )}
            </Button>
          </>
        }
        onClear={onClear}
        classNames={{
          inputWrapper: [
            'border-divider',
            'data-[hover=true]:border-secondary',
            'data-[focus=true]:border-primary',
            isFocused ? 'shadow-medium' : '',
            'transition-all duration-200',
            isCustomMdPlus ? 'h-[44px] min-h-[44px]' : '',
          ].join(' '),
          input:
            value && value?.length > 0
              ? 'text-default-500'
              : 'placeholder:text-[#9e9e9e]',
          label: 'text-sm',
        }}
        {...rest}
      />
    );
  },
);

PasswordInput.displayName = 'PasswordInput';
export default PasswordInput;
